/**
 * Types for GET /mgmt/Entitlements/entity response.
 * Used to drive nav visibility for non-imperator users (filter by ReadEntity and entity Id).
 */

import { createEntityRef } from './entity/EntityRef';

export interface EntityEntitlementsRequestor {
    Id: string;
    EntityType: string;
    DisplayName: string;
}

export interface EntityEntitlementsPolicyRuleRef {
    Id: string;
    EntityType: string;
    DisplayName: string;
}

export interface EntityEntitlementItem {
    Id: string;
    DisplayName: string;
    Description?: string;
    Notes?: string;
    Disjointed: boolean;
    DeleteEntity: boolean;
    CreateEntity: boolean;
    ModifyEntity: boolean;
    ReadEntity: boolean;
    Count?: number;
    EntitySets: string[];
    PolicyRules: EntityEntitlementsPolicyRuleRef[];
    /** When present, only these attribute IDs may be used for search; when absent, all attributes are allowed. */
    ReadableAttributeIds?: string[];
}

/** Entity set name -> list of entity type IDs in that set (e.g. Person -> ["Applicant", "Employee", ...]) */
export type EntityEntitlementsEntitySets = Record<string, string[]>;

/** Legacy: pivot-level stat when tier1 included full tree (replaced by RequestApprovalTypeSummary + expand-on-demand). */
export interface PendingRequestApprovalStat {
    Pivot: {
        Id: string;
        EntityType: string;
        DisplayName: string;
    };
    PendingCount: number;
}

/** Legacy: full approval + stats from tier1 (replaced by RequestApprovalTypeSummary). */
export interface PendingRequestApproval {
    Id: string;
    DisplayName: string;
    TotalPending: number;
    Stats: PendingRequestApprovalStat[];
}

/** Tier1 request-approval summary item (next level loaded on expand via API). */
export interface RequestApprovalTypeSummaryItem {
    Id: string;
    DisplayName: string;
    Type: string;
    Counts: {
        Pending: number;
    };
}

export type RequestApprovalTypeSummary = RequestApprovalTypeSummaryItem[];

/** Item from GET .../entities/requestapprovaltypes/{id}/$pivot (next layer when expanding). */
export interface RequestApprovalTypePivotListItem {
    $Summary?: RequestApprovalTypeSummaryItem;
    Id: string;
    EntityType: string;
    DisplayName: string;
}

export interface EntityEntitlementsResponse {
    Requestor: EntityEntitlementsRequestor;
    IsAdministrator: boolean;
    EntitySets: EntityEntitlementsEntitySets;
    EntityEntitlements: EntityEntitlementItem[];
    /** Legacy: full tree; prefer RequestApprovalTypeSummary. */
    PendingRequestApprovals?: PendingRequestApproval[];
    /** Tier1 request-approval summary (expand one to load next layer via API). */
    RequestApprovalTypeSummary?: RequestApprovalTypeSummary;
}

/** Helper: set of entity type IDs the user is entitled to read (for nav filtering when not administrator). */
export function getReadableEntityIds(response: EntityEntitlementsResponse | null): Set<string> {
    if (!response?.EntityEntitlements) return new Set();
    const set = new Set<string>();
    for (const item of response.EntityEntitlements) {
        if (item.ReadEntity) set.add(item.Id);
    }
    return set;
}

/** Helper: set of entity type IDs that are disjointed (tier1 EntityEntitlements[].Disjointed === true). Used to show * on nav items. */
export function getDisjointedEntityIds(response: EntityEntitlementsResponse | null): Set<string> {
    if (!response?.EntityEntitlements) return new Set();
    const set = new Set<string>();
    for (const item of response.EntityEntitlements) {
        if (item.Disjointed === true && item.Id) set.add((item.Id as string).trim().toLowerCase());
    }
    return set;
}

/** Helper: whether the current requestor is an administrator (sees everything). */
export function isAdministrator(response: EntityEntitlementsResponse | null): boolean {
    return response?.IsAdministrator === true;
}

/** Helper: whether the user can see "All Users" (Person) - has ReadEntity for at least one Person entity set type. */
export function canReadPerson(response: EntityEntitlementsResponse | null, readableEntityIds: Set<string>): boolean {
    if (!response?.EntitySets?.Person?.length) return false;
    return response.EntitySets.Person.some(id => readableEntityIds.has(id));
}

/**
 * Entity type IDs that belong to the "Person" entity set from tier1.
 * Use this to determine if an entity type is a person type (e.g. Applicant, Contractor, Employee, Public, Shared, Sponsored, Student, Vacancy).
 * When tier1 is not loaded or has no Person set, returns [].
 */
export function getPersonEntityTypes(response: EntityEntitlementsResponse | null): string[] {
    return response?.EntitySets?.Person ?? [];
}

/** Helper: whether the user can create entities of this type (tier2). Admins can always create. */
export function canCreateEntity(
    response: EntityEntitlementsResponse | null,
    schemaEntityId: string
): boolean {
    if (!response) return false;
    if (response.IsAdministrator === true) return true;
    if (!schemaEntityId?.trim()) return false;
    const id = schemaEntityId.trim().toLowerCase();
    const item = response.EntityEntitlements?.find(
        e => (e.Id ?? '').toString().trim().toLowerCase() === id
    );
    return item?.CreateEntity === true;
}

/** Attribute item in per-entity entitlement response (tier2 AttributeBindings[].Attribute). */
export interface EntityEntitlementAttributeItem {
    Id?: string;
    DisplayName?: string;
    Type?: string;
    Description?: string;
    Array?: boolean;
    Searchable?: boolean;
    Indexed?: boolean;
    Encrypted?: boolean;
    IsReference?: boolean;
    /** When reference: entity/attribute types this can point to (e.g. SchemaAttribute for attribute refs). */
    IsReferenceTo?: string[];
    [key: string]: unknown;
}

/** Binding for one attribute in tier2 AttributeBindings[].Binding. */
export interface EntityEntitlementSchemaBinding {
    /** Attribute ID (e.g. "$.AccountName"). */
    Attribute?: string;
    Required?: boolean;
    ReadOnly?: boolean;
    AllowedValues?: unknown[];
    RegEx?: string;
    [key: string]: unknown;
}

/** Single item in tier2 AttributeBindings array. */
export interface EntityEntitlementSchemaAttributeBinding {
    Attribute: EntityEntitlementAttributeItem;
    Binding: EntityEntitlementSchemaBinding;
}

/** Use attribute id as-is (no stripping). */
function tier2AttrIdToName(id: string): string {
    return (id ?? '').toString().trim();
}

/** Form-attribute shape expected by GenericEntityUpsertDialog (matches schema field options). */
export interface FormAttributeOption {
    name: string;
    id: string;
    value: string;
    label: string;
    type: string;
    required: boolean;
    multiValued: boolean;
    isReference: boolean;
    computed: boolean;
    /** When reference: types this can point to (e.g. ['SchemaAttribute'] for attribute refs). From tier2 IsReferenceTo. */
    isReferenceTo?: string[];
    /** When true, field is shown but not editable (e.g. readable but not settable per tier3). */
    readOnly?: boolean;
    /** Optional description from schema/tier2/tier3 (shown in field label tooltip). */
    description?: string;
}

/**
 * Build form-attribute shape from tier2 Attributes array (for create when schema is empty).
 */
export function buildFormAttributesFromTier2Attributes(
    attributes: EntityEntitlementAttributeItem[] | undefined
): FormAttributeOption[] {
    if (!Array.isArray(attributes) || attributes.length === 0) return [];
    return attributes
        .map((attr): FormAttributeOption | null => {
            const name = tier2AttrIdToName(attr.Id ?? attr.DisplayName ?? '');
            if (!name) return null;
            const type = (attr.Type ?? 'string').toString().toLowerCase();
            return {
                name,
                id: name,
                value: name,
                type,
                label: attr.DisplayName ?? name,
                required: false,
                multiValued: !!attr.Array,
                isReference: !!attr.IsReference,
                computed: false,
                isReferenceTo: Array.isArray(attr.IsReferenceTo) ? attr.IsReferenceTo : undefined,
                description: attr.Description,
            };
        })
        .filter((a): a is FormAttributeOption => a != null);
}

/**
 * Build form-attribute shape from tier2 AttributeBindings (preferred when API sends it).
 * Uses Binding.Required and Attribute.Array.
 */
export function buildFormAttributesFromTier2AttributeBindings(
    attributeBindings: EntityEntitlementSchemaAttributeBinding[] | undefined
): FormAttributeOption[] {
    if (!Array.isArray(attributeBindings) || attributeBindings.length === 0) return [];
    return attributeBindings
        .map((ab): FormAttributeOption | null => {
            const attr = ab.Attribute;
            if (!attr || typeof attr !== 'object') return null;
            const name = tier2AttrIdToName(attr.Id ?? attr.DisplayName ?? '');
            if (!name) return null;
            const type = (attr.Type ?? 'string').toString().toLowerCase();
            const binding = ab.Binding;
            return {
                name,
                id: name,
                value: name,
                type,
                label: attr.DisplayName ?? name,
                required: binding?.Required === true,
                multiValued: !!attr.Array,
                isReference: !!attr.IsReference,
                isReferenceTo: Array.isArray(attr.IsReferenceTo) ? attr.IsReferenceTo : undefined,
                computed: false,
                description: attr.Description,
            };
        })
        .filter((a): a is FormAttributeOption => a != null);
}

/**
 * Get attributes array from tier2 response (from AttributeBindings or legacy Attributes).
 * When Entity.IsEntitySet is true (facade), only Attributes is used; AttributeBindings are not available.
 */
export function getTier2AttributesFromResponse(
    response: EntityEntitlementForSchemaResponse | null | undefined
): EntityEntitlementAttributeItem[] {
    if (!response) return [];
    if (isEntitySetFacade(response)) return Array.isArray(response.Attributes) ? response.Attributes : [];
    const bindings = response.AttributeBindings;
    if (Array.isArray(bindings) && bindings.length > 0) return bindings.map((ab) => ab.Attribute);
    return Array.isArray(response.Attributes) ? response.Attributes : [];
}

/** True when tier2/form attribute is a reference to schema attribute(s), so UI should show attribute list not entity picker. */
export function isReferenceToSchemaAttribute(attr: { isReferenceTo?: unknown } | null | undefined): boolean {
    const refTo = attr?.isReferenceTo;
    if (!Array.isArray(refTo) || refTo.length === 0) return false;
    return refTo.some((item: unknown) => {
        const s = typeof item === 'string' ? item : String(item ?? '');
        const lower = s.toLowerCase();
        return lower === 'schemaattribute' || lower.includes('schemaattribute');
    });
}

/** Entity summary in tier2 response (Entity property). */
export interface EntityEntitlementSchemaEntity {
    Id: string;
    DisplayName: string;
    Description?: string;
    Invokable?: boolean;
    Disjointed?: boolean;
    /** When true, this is a facade (e.g. Person as "All Users" set); AttributeBindings are not available. */
    IsEntitySet?: boolean;
    [key: string]: unknown;
}

/** Response from GET /mgmt/Entitlements/entity/{schemaEntityId} (per-entity entitlement for table view). */
export interface EntityEntitlementForSchemaResponse {
    Requestor?: EntityEntitlementsRequestor;
    IsAdministrator?: boolean;
    CreateEntity?: boolean;
    DeleteEntity?: boolean;
    /** When true, user can edit entities of this type (table row pencil → go straight to edit). */
    EditEntity?: boolean;
    /** Legacy: when present, only these attribute IDs may be shown (table + Search In). */
    ReadableAttributeIds?: string[];
    /** Entity type summary (Id, DisplayName, Invokable, Disjointed, Description). */
    Entity?: EntityEntitlementSchemaEntity;
    /** Attribute list with bindings (Required, RegEx, AllowedValues) per attribute. */
    AttributeBindings?: EntityEntitlementSchemaAttributeBinding[];
    /** Legacy: plain attribute list when API does not send AttributeBindings. */
    Attributes?: EntityEntitlementAttributeItem[];
    PolicyRules?: EntityEntitlementsPolicyRuleRef[];
    [key: string]: unknown;
}

/** Whether the user can edit rows of this entity type from the table (show pencil, skip view). Uses tier2 EditEntity. */
export function canEditFromPerEntityResponse(response: EntityEntitlementForSchemaResponse | null): boolean {
    if (response == null) return false; // not loaded yet: hide pencil until entitlement is resolved
    if (response.IsAdministrator === true) return true;
    return response.EditEntity === true;
}

/** True when tier2 response is for an entity-set facade (e.g. Person); AttributeBindings are not available. */
export function isEntitySetFacade(response: EntityEntitlementForSchemaResponse | null | undefined): boolean {
    return response?.Entity?.IsEntitySet === true;
}

/** Target or requestor reference in tier3 response. */
export interface EntityEntitlementsTargetRef {
    Id: string;
    EntityType: string;
    DisplayName: string;
}

/** Attribute metadata in TargetEntitlements. */
export interface EntityEntitlementTargetAttribute {
    Id: string;
    DisplayName: string;
    Type?: string;
    Description?: string;
    Array?: boolean;
    Searchable?: boolean;
    Indexed?: boolean;
    Encrypted?: boolean;
    HasLengthCount?: boolean;
    Count?: number;
    Length?: number;
    IsReference?: boolean;
    IsReferenceTo?: string[];
    [key: string]: unknown;
}

/** Binding constraints per attribute (tier3 TargetEntitlements[].Binding). */
export interface EntityEntitlementTargetBinding {
    Required?: boolean;
    ReadOnly?: boolean;
    AllowedValues?: unknown[];
    RegEx?: string;
    [key: string]: unknown;
}

/** Single entitlement for an attribute in tier3 response. */
export interface EntityEntitlementTargetItem {
    Attribute: EntityEntitlementTargetAttribute;
    /** Binding constraints (RegEx, Required, AllowedValues). Present for each attribute. */
    Binding?: EntityEntitlementTargetBinding;
    SetValue: boolean;
    AddValue: boolean;
    RemoveValue: boolean;
    DeleteValue: boolean;
    ReadValue: boolean;
    /** When true, the current requestor may call mgmt/encdec for this attribute (authoritative decrypt entitlement). */
    Decrypt?: boolean;
    /** Operations relevant to this attribute shape; do not treat as requestor entitlements (use flags such as {@link Decrypt}, {@link SetValue}). */
    ApplicableOperations?: string[];
    PolicyRules: EntityEntitlementsPolicyRuleRef[];
}


/**
 * Response from GET /mgmt/Entitlements/entity/{schemaEntityId}/{targetId} (tier3 / detail-view entitlement).
 * Restricted view for the current employee (on-behalf-of) — drives what the user can read/write for that entity.
 * Attribute-level data lives under TargetEntitlements (each item: Attribute, Binding, SetValue, ReadValue, etc.).
 * Legacy: some APIs may still send a flat Attributes list; use getTargetEntitlementsList() to normalize.
 * @see src/samples/restricted-view-entitlement-response.json
 */
export interface EntityEntitlementForTargetResponse {
    Target?: EntityEntitlementsTargetRef;
    Requestor?: EntityEntitlementsRequestor;
    IsAdministrator?: boolean;
    PolicyRules?: EntityEntitlementsPolicyRuleRef[];
    TimeTaken?: string;
    /** Attribute-level entitlements (preferred). Each item has Attribute, Binding, SetValue, ReadValue, etc. */
    TargetEntitlements?: EntityEntitlementTargetItem[];
    /** Legacy: flat attribute list; superseded by TargetEntitlements. Use getTargetEntitlementsList() for both. */
    Attributes?: unknown[];
    /** When true, user can create this entity type. */
    CreateEntity?: boolean;
    /** When true, user can delete this entity (detail view Delete button). */
    DeleteEntity?: boolean;
    [key: string]: unknown;
}

/**
 * Return the list of tier3 attribute entitlement items, from TargetEntitlements or legacy Attributes.
 * Use this so code works whether the API sends TargetEntitlements or the old Attributes shape.
 */
export function getTargetEntitlementsList(
    response: EntityEntitlementForTargetResponse | null | undefined
): EntityEntitlementTargetItem[] {
    if (!response) return [];
    const list = response.TargetEntitlements;
    if (Array.isArray(list) && list.length > 0) return list;
    const legacy = (response as { Attributes?: unknown[] }).Attributes;
    if (!Array.isArray(legacy) || legacy.length === 0) return [];
    return legacy.map((item: any): EntityEntitlementTargetItem => {
        if (item && typeof item === 'object' && item.Attribute != null) {
            return item as EntityEntitlementTargetItem;
        }
        const attr = item && typeof item === 'object' ? item : {};
        return {
            Attribute: {
                Id: attr.Id ?? attr.id,
                DisplayName: attr.DisplayName ?? attr.displayName,
                Type: attr.Type ?? attr.type
            },
            Binding: attr.Binding ?? attr.binding,
            SetValue: attr.SetValue === true,
            AddValue: attr.AddValue === true,
            RemoveValue: attr.RemoveValue === true,
            DeleteValue: attr.DeleteValue === true,
            ReadValue: attr.ReadValue === true,
            PolicyRules: Array.isArray(attr.PolicyRules) ? attr.PolicyRules : []
        };
    });
}

/**
 * Whether the user can delete this target entity from the detail view (tier3 DeleteEntity).
 * When tier3 is not loaded (null/undefined), returns true so delete is allowed if parent provided onDelete.
 */
export function canDeleteFromTargetResponse(response: EntityEntitlementForTargetResponse | null | undefined): boolean {
    if (response == null) return true;
    if (response.IsAdministrator === true) return true;
    return response.DeleteEntity === true;
}

/**
 * Whether the user can create (e.g. clone) from this target's tier3 response (CreateEntity).
 * Clone button is enabled if and only if this returns true.
 */
export function canCreateFromTargetResponse(response: EntityEntitlementForTargetResponse | null | undefined): boolean {
    if (response == null) return false;
    if (response.IsAdministrator === true) return true;
    return response.CreateEntity === true;
}

/**
 * Whether the user can approve this request entity from tier3 response.
 *
 * Backend wiring is still in progress; the UI checks a small set of likely
 * property names so it can light up immediately once the API adds the flag.
 */
export function canApproveRequestFromTargetResponse(response: EntityEntitlementForTargetResponse | null | undefined): boolean {
    if (response == null) return false;
    if (response.IsAdministrator === true) return true;
    const r: any = response as any;
    return r.IsAuthorizedActor === true;
}

/**
 * Return attribute id as-is (no stripping of $. prefix).
 */
export function attributeIdToName(id: string): string {
    return (id ?? '').toString().trim();
}

/**
 * Return a name safe for the attributes= request param (APIs expect plain names like "Config", not "$.Config").
 * Use when building the list of attribute names to send in entity detail/list requests.
 */
export function attributeDisplayNameForRequest(name: string): string {
    const s = (name ?? '').toString().trim();
    return s.startsWith('$.') ? s.substring(2) : s;
}

/** Binding constraints for a single attribute (for validation warnings on view/edit). */
export interface BindingConstraints {
    AllowedValues?: unknown[];
    RegEx?: string;
}

/** Format binding constraints as a short summary for tooltips in view mode. */
export function getBindingConstraintsSummary(constraints: BindingConstraints | undefined): string {
    if (!constraints) return '';
    const parts: string[] = [];
    if (Array.isArray(constraints.AllowedValues) && constraints.AllowedValues.length > 0) {
        const maxShow = 8;
        const vals = constraints.AllowedValues.slice(0, maxShow).map((v) => String(v ?? '').trim()).filter(Boolean);
        const rest = constraints.AllowedValues.length - maxShow;
        const allowedStr = rest > 0
            ? `Allowed values: ${vals.join(', ')} and ${rest} more`
            : `Allowed values: ${vals.join(', ')}`;
        parts.push(allowedStr);
    }
    if (constraints.RegEx != null && String(constraints.RegEx).trim() !== '') {
        parts.push(`RegEx: ${String(constraints.RegEx).trim()}`);
    }
    return parts.join('. ') || '';
}

/**
 * Build a map of attribute name (normalized) -> BindingConstraints from tier3 TargetEntitlements.
 * Used to drive view/edit warnings when value is not in AllowedValues or doesn't match RegEx.
 */
export function getBindingConstraintsFromTargetEntitlements(
    targetEntitlement: EntityEntitlementForTargetResponse | null | undefined
): Record<string, BindingConstraints> {
    const map: Record<string, BindingConstraints> = {};
    const list = getTargetEntitlementsList(targetEntitlement);
    if (!list.length) return map;
    for (const e of list) {
        const name = attributeIdToName(e.Attribute?.Id ?? e.Attribute?.DisplayName ?? '');
        if (!name) continue;
        const b = e.Binding;
        if (!b) continue;
        const hasAllowed = Array.isArray(b.AllowedValues) && b.AllowedValues.length > 0;
        const hasRegex = b.RegEx != null && String(b.RegEx).trim() !== '';
        if (!hasAllowed && !hasRegex) continue;
        const entry: BindingConstraints = {
            ...(hasAllowed && { AllowedValues: b.AllowedValues }),
            ...(hasRegex && { RegEx: String(b.RegEx).trim() })
        };
        map[name] = entry;
        map[name.toLowerCase()] = entry;
    }
    return map;
}

/**
 * Build a map of attribute name -> BindingConstraints from tier2 AttributeBindings.
 */
export function getBindingConstraintsFromTier2AttributeBindings(
    attributeBindings: EntityEntitlementSchemaAttributeBinding[] | undefined
): Record<string, BindingConstraints> {
    const map: Record<string, BindingConstraints> = {};
    if (!Array.isArray(attributeBindings)) return map;
    for (const ab of attributeBindings) {
        const attr = ab.Attribute;
        if (!attr) continue;
        const name = attributeIdToName(attr.Id ?? attr.DisplayName ?? '');
        if (!name) continue;
        const b = ab.Binding;
        if (!b) continue;
        const hasAllowed = Array.isArray(b.AllowedValues) && b.AllowedValues.length > 0;
        const hasRegex = b.RegEx != null && String(b.RegEx).trim() !== '';
        if (!hasAllowed && !hasRegex) continue;
        const entry: BindingConstraints = {
            ...(hasAllowed && { AllowedValues: b.AllowedValues }),
            ...(hasRegex && { RegEx: String(b.RegEx).trim() })
        };
        map[name] = entry;
        map[name.toLowerCase()] = entry;
    }
    return map;
}

/**
 * Check if a display value violates binding constraints (not in AllowedValues or doesn't match RegEx).
 * Returns a short message when there is a violation, or null.
 */
export function getBindingViolationMessage(
    value: unknown,
    constraints: BindingConstraints | undefined
): string | null {
    if (!constraints) return null;
    const str = value == null ? '' : String(value).trim();
    if (constraints.RegEx) {
        try {
            const re = new RegExp(constraints.RegEx, 'i');
            if (str !== '' && !re.test(str)) return `Value does not match required pattern.`;
        } catch {
            return null;
        }
    }
    if (Array.isArray(constraints.AllowedValues) && constraints.AllowedValues.length > 0) {
        const allowedLower = new Set(
            constraints.AllowedValues.map((v) => String(v ?? '').toLowerCase())
        );
        if (str !== '' && !allowedLower.has(str.toLowerCase())) return `Value is not in the allowed list.`;
    }
    return null;
}

/**
 * Get attribute names the requestor can read for this target entity.
 * Returns undefined when no restriction (e.g. admin), [] when no access, or string[] of allowed names.
 */
export function getReadableAttributeIdsFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] | undefined {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return undefined;
    const names = list
        .filter((e) => tier3ItemReadValue(e))
        .map((e) => attributeIdToName(e.Attribute?.Id ?? e.Attribute?.DisplayName ?? ''))
        .filter(Boolean);
    return names;
}

/** Readable attribute with id, displayName, type and indexed flag (for detail view). Use id for logic, displayName for labels. */
export interface ReadableAttributeWithType {
    id: string;
    displayName: string;
    /** @deprecated Use id for lookup; kept for compatibility during migration. */
    name: string;
    type: string;
    /** When false, value may be large or a file; detail view shows a stub and API should not request it by default. */
    indexed?: boolean;
    /** When true, value is multi-valued (array) even if Type is "string". */
    array?: boolean;
    /** When true, attribute items represent references. */
    isReference?: boolean;
    /** Tier3 Count (when HasLengthCount). */
    count?: number;
    /** Tier3 Length (when HasLengthCount). */
    length?: number;
}

/**
 * Build a projection object for tier3-driven view: keys are tier3 attribute ids, values are read from the entity.
 * Lookup by id (or displayName) without mutating the entity, so raw JSON tab and elsewhere see no duplicate Id/id.
 * Value is read by trying entity[id], entity[displayName], and entity[id without '$.' prefix].
 */
export function buildTier3ProjectionForEntity(
    entity: any,
    response: EntityEntitlementForTargetResponse | null | undefined
): Record<string, unknown> {
    const projection: Record<string, unknown> = {};
    const list = getTargetEntitlementsList(response);
    if (!entity || typeof entity !== 'object' || !list.length) return projection;
    for (const e of list) {
        if (!tier3ItemReadValue(e) || !e.Attribute) continue;
        const attr = e.Attribute;
        const id = attributeIdToName(attr.Id ?? attr.DisplayName ?? '');
        if (!id) continue;
        const displayName = (attr.DisplayName ?? '').toString().trim();
        let value: unknown = entity[id];
        if (value === undefined && displayName) value = entity[displayName];
        if (value === undefined && id.startsWith('$.')) value = entity[id.substring(2)];
        if (value === undefined && !id.startsWith('$.') && displayName) value = entity['$.' + id];
        projection[id] = value;
    }
    return projection;
}

/**
 * Get readable attributes with id, displayName, type and indexed from tier3.
 * Id is required (attributes without Id are skipped). Use id for decision/API logic, displayName for labels.
 */
export function getReadableAttributesWithTypesFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): ReadableAttributeWithType[] | undefined {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return undefined;
    return list
        .filter((e) => tier3ItemReadValue(e) && e.Attribute && (e.Attribute.Id ?? '').toString().trim() !== '')
        .map((e) => {
            const attr = e.Attribute!;
            const id = attributeIdToName(attr.Id!);
            const displayName = (attr.DisplayName ?? '').toString().trim() || id;
            const type = (attr.Type ?? 'string').toString().toLowerCase();
            const indexed = attr.Indexed;
            return {
                id,
                displayName,
                name: id,
                type,
                indexed,
                array: attr.Array === true,
                isReference: attr.IsReference === true,
                count: typeof attr.Count === 'number' ? attr.Count : undefined,
                length: typeof attr.Length === 'number' ? attr.Length : undefined,
            };
        })
        .filter((a) => a.id.length > 0);
}

/**
 * Get readable attribute names from tier3 where Attribute.Indexed is true (or omitted/truthy).
 * Use this for API attribute requests so we do not request large or file-backed non-indexed properties.
 * Returns undefined when no tier3 restriction; otherwise string[] of attribute names to request.
 */
export function getIndexedReadableAttributeIdsFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] | undefined {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return undefined;
    const names = list
        .filter((e) => e.ReadValue === true && e.Attribute && e.Attribute.Indexed !== false)
        .map((e) => attributeIdToName(e.Attribute!.Id ?? e.Attribute!.DisplayName ?? ''))
        .filter(Boolean);
    return names;
}

/**
 * Get displayNames for indexed readable attributes from tier3, for the attributes= request param.
 * APIs accept displayName in attributes=, not id. Uses Attribute.DisplayName when present;
 * otherwise derives from Id by stripping leading '$.' only for this param.
 */
export function getIndexedReadableAttributeDisplayNamesFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] | undefined {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return undefined;
    return list
        .filter((e) => e.ReadValue === true && e.Attribute && e.Attribute.Indexed !== false)
        .map((e) => {
            const attr = e.Attribute!;
            if (attr.DisplayName != null && String(attr.DisplayName).trim() !== '') {
                return attributeDisplayNameForRequest(String(attr.DisplayName).trim());
            }
            const id = attributeIdToName(attr.Id ?? '');
            return attributeDisplayNameForRequest(id);
        })
        .filter(Boolean);
}

/**
 * Get readable attribute names from tier3 where Attribute.Indexed is false.
 * Used for debug: when requesting only indexed attrs, these non-indexed properties would require a full attribute list if we wanted to load them.
 */
export function getNonIndexedReadableAttributeNamesFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] {
    const withTypes = getReadableAttributesWithTypesFromTargetResponse(response);
    if (!withTypes?.length) return [];
    return withTypes.filter((a) => a.indexed === false).map((a) => a.name).filter(Boolean);
}

/**
 * Get readable attribute names from tier3 where Indexed is false and HasLengthCount is true.
 * When the entity has no value for such an attribute, the field has not been loaded; show delay-load (Not loaded* + Load) in forms.
 */
export function getNonIndexedHasLengthCountAttributeNamesFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    return list
        .filter((e) => e.ReadValue === true && e.Attribute && e.Attribute.Indexed === false && e.Attribute.HasLengthCount === true)
        .map((e) => attributeIdToName(e.Attribute!.Id ?? e.Attribute!.DisplayName ?? ''))
        .filter(Boolean);
}

/**
 * Max length (characters) for eager-loading non-indexed HasLengthCount attributes in the entity detail request.
 * When tier3 reports Length at or below this, we include the attribute in the initial fetch; otherwise delay-load.
 */
export const EAGER_LOAD_NON_INDEXED_MAX_LENGTH = 448;

/**
 * Format tier3 Length/Count for delay-load placeholders ("Not loaded* (≈ …)").
 * Length at or below {@link EAGER_LOAD_NON_INDEXED_MAX_LENGTH} is shown as chars; larger values as B/KB/MB.
 */
export function formatTier3LengthCountSizeEstimate(
    length: number | null | undefined,
    count: number | null | undefined
): string | null {
    if (typeof length === 'number' && length >= 0) {
        if (length <= EAGER_LOAD_NON_INDEXED_MAX_LENGTH) return `≈ ${length} chars`;
        if (length >= 1024 * 1024) return `≈ ${(length / (1024 * 1024)).toFixed(1)} MB`;
        if (length >= 1024) return `≈ ${(length / 1024).toFixed(1)} KB`;
        return `≈ ${length} B`;
    }
    if (typeof count === 'number' && count >= 0) return `≈ ${count} items`;
    return null;
}

/**
 * Get display names for non-indexed readable attributes from tier3 where HasLengthCount is true.
 * Used to eager-load these attributes in the entity detail request (attributes= param uses display names).
 * Keeps the initial load small by only including non-indexed attrs that report a length (small enough to fetch).
 */
export function getNonIndexedHasLengthCountAttributeDisplayNamesFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    return list
        .filter((e) => e.ReadValue === true && e.Attribute && e.Attribute.Indexed === false && e.Attribute.HasLengthCount === true)
        .map((e) => {
            const attr = e.Attribute!;
            if (attr.DisplayName != null && String(attr.DisplayName).trim() !== '') {
                return attributeDisplayNameForRequest(String(attr.DisplayName).trim());
            }
            const id = attributeIdToName(attr.Id ?? '');
            return attributeDisplayNameForRequest(id);
        })
        .filter(Boolean);
}

/**
 * Get display names for non-indexed readable attributes from tier3 where HasLengthCount is true and Length is at or below EAGER_LOAD_NON_INDEXED_MAX_LENGTH.
 * Used for the entity detail fetch (attributes=) so we eager-load only "small" non-indexed attributes; larger ones stay delay-load.
 * When Length is missing, the attribute is not included (we don't eager-load unknown size).
 */
export function getNonIndexedHasLengthCountAttributeDisplayNamesForEagerLoad(
    response: EntityEntitlementForTargetResponse | null
): string[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    const maxLen = EAGER_LOAD_NON_INDEXED_MAX_LENGTH;
    return list
        .filter((e) => {
            if (e.ReadValue !== true || !e.Attribute) return false;
            const attr = e.Attribute;
            if (attr.Indexed !== false || attr.HasLengthCount !== true) return false;
            const len = attr.Length;
            if (len == null || typeof len !== 'number') return false;
            return len <= maxLen;
        })
        .map((e) => {
            const attr = e.Attribute!;
            if (attr.DisplayName != null && String(attr.DisplayName).trim() !== '') {
                return attributeDisplayNameForRequest(String(attr.DisplayName).trim());
            }
            const id = attributeIdToName(attr.Id ?? '');
            return attributeDisplayNameForRequest(id);
        })
        .filter(Boolean);
}

/**
 * Count threshold for array+reference attributes: when tier3 Count is above this, do not request on initial load (delay-load only).
 * Avoids huge payloads and UI explosion for e.g. ManualSetMember with thousands of members.
 */
export const ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD = 50;

/**
 * Get readable attribute names from tier3 where Array is true, IsReference is true, and Count > ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD.
 * These are shown as delay-load (stub + Load) in forms; do not include them in the initial entity detail request.
 */
export function getLargeArrayRefAttributeNamesFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    const threshold = ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD;
    return list
        .filter((e) => {
            if (e.ReadValue !== true || !e.Attribute) return false;
            const attr = e.Attribute;
            return attr.Array === true && attr.IsReference === true &&
                typeof attr.Count === 'number' && attr.Count > threshold;
        })
        .map((e) => attributeIdToName(e.Attribute!.Id ?? e.Attribute!.DisplayName ?? ''))
        .filter(Boolean);
}

/**
 * Get readable attribute names from tier3 that are Array + IsReference and are deferred (stub) in the form:
 * either Count > threshold (large) or non-indexed + HasLengthCount (e.g. Group Member).
 * Use this set to show "View / Add items / Remove items" for any array-ref that appears as "Not loaded*",
 * so Group members and other large ref lists get distinct Add vs Remove instead of only "Load" or "View".
 */
export function getDeferredArrayRefAttributeNamesFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    const threshold = ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD;
    return list
        .filter((e) => {
            if (e.ReadValue !== true || !e.Attribute) return false;
            const attr = e.Attribute;
            if (attr.Array !== true || attr.IsReference !== true) return false;
            const countOverThreshold = typeof attr.Count === 'number' && attr.Count > threshold;
            const nonIndexedHasLengthCount = attr.Indexed === false && attr.HasLengthCount === true;
            return countOverThreshold || nonIndexedHasLengthCount;
        })
        .map((e) => attributeIdToName(e.Attribute!.Id ?? e.Attribute!.DisplayName ?? ''))
        .filter(Boolean);
}

/**
 * Get display names for the same attributes as getLargeArrayRefAttributeNamesFromTargetResponse.
 * Use to exclude these from the attributes= param on the initial entity detail request.
 */
export function getLargeArrayRefAttributeDisplayNamesToExcludeFromEagerLoad(
    response: EntityEntitlementForTargetResponse | null
): string[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    const threshold = ARRAY_REF_DELAY_LOAD_COUNT_THRESHOLD;
    return list
        .filter((e) => {
            if (e.ReadValue !== true || !e.Attribute) return false;
            const attr = e.Attribute;
            return attr.Array === true && attr.IsReference === true &&
                typeof attr.Count === 'number' && attr.Count > threshold;
        })
        .map((e) => {
            const attr = e.Attribute!;
            if (attr.DisplayName != null && String(attr.DisplayName).trim() !== '') {
                return attributeDisplayNameForRequest(String(attr.DisplayName).trim());
            }
            return attributeDisplayNameForRequest(attributeIdToName(attr.Id ?? ''));
        })
        .filter(Boolean);
}

/**
 * Get the tier3 entitlement item for a specific attribute by id (case-insensitive exact match).
 * Uses createEntityRef().displayName so "$.DisplayName" and "DisplayName" both match.
 */
export function getTargetEntitlementItemForAttribute(
    response: EntityEntitlementForTargetResponse | null | undefined,
    attributeName: string
): EntityEntitlementTargetItem | null {
    const list = getTargetEntitlementsList(response);
    if (!list.length || !attributeName?.trim()) return null;
    const want = createEntityRef(attributeName.trim()).displayName.trim().toLowerCase();
    const found = list.find((e) => {
        const raw = (e.Attribute?.Id ?? e.Attribute?.DisplayName ?? '').toString().trim();
        const name = raw ? createEntityRef(raw).displayName.trim().toLowerCase() : '';
        return name === want;
    });
    return found ?? null;
}

/**
 * Human-readable size estimate from tier3 for a named attribute (delay-loaded placeholders).
 */
export function formatTier3AttributeSizeEstimate(
    attributeName: string,
    response: EntityEntitlementForTargetResponse | null | undefined
): string | null {
    const attr = getTargetEntitlementItemForAttribute(response, attributeName)?.Attribute;
    if (!attr) return null;
    return formatTier3LengthCountSizeEstimate(attr.Length, attr.Count);
}

/**
 * Get the attribute display name for use in property-path APIs (e.g. .../entities/{entityType}/{entityId}/{propertyAttributeDisplayName}).
 * Prefers Attribute.DisplayName, then Attribute.Id; normalizes with attributeDisplayNameForRequest. Falls back to normalized attributeName.
 */
export function getAttributeDisplayNameForPropertyPath(
    response: EntityEntitlementForTargetResponse | null | undefined,
    attributeName: string
): string {
    const item = getTargetEntitlementItemForAttribute(response, attributeName);
    const attr = item?.Attribute;
    if (attr) {
        const raw = (attr.DisplayName ?? attributeIdToName(attr.Id ?? '')).toString().trim();
        return raw ? attributeDisplayNameForRequest(raw) : attributeDisplayNameForRequest((attributeName ?? '').toString().trim().replace(/^\$\./, ''));
    }
    return attributeDisplayNameForRequest((attributeName ?? '').toString().trim().replace(/^\$\./, ''));
}

const REGEX_DISPLAY_MAX_LENGTH = 30;
/** When AllowedValues count is at or below this, show the actual values in the tooltip. */
const ALLOWED_VALUES_DISPLAY_THRESHOLD = 10;
const ALLOWED_VALUE_ITEM_MAX_LENGTH = 50;

function normalizeAttrNameForMatch(name: string): string {
    return (name ?? '').toString().trim().replace(/^\$\./, '').toLowerCase();
}

/**
 * Get attribute names that are read-only from tier2 (Binding.ReadOnly === true).
 * Returns plain names (no "$." prefix) for use when building create payload so they can be excluded.
 * Uses createEntityRef(id).displayName; invalid or empty ids will throw.
 */
export function getReadOnlyAttributeNamesFromTier2(
    response: EntityEntitlementForSchemaResponse | null | undefined
): string[] {
    if (!response) return [];
    const names: string[] = [];
    const bindings = response.AttributeBindings;
    if (Array.isArray(bindings)) {
        for (const ab of bindings) {
            if ((ab.Binding as EntityEntitlementSchemaBinding)?.ReadOnly !== true) continue;
            const attr = ab.Attribute;
            if (!attr) continue;
            names.push(createEntityRef(String(attr.Id ?? attr.DisplayName ?? '')).displayName);
        }
        return names;
    }
    return names;
}

/**
 * Get attribute names that are read-only from tier3 (Binding.ReadOnly === true).
 * Returns plain names (no "$." prefix) for use when building create payload so they can be excluded.
 * Uses createEntityRef(id).displayName; invalid or empty ids will throw.
 */
export function getReadOnlyAttributeNamesFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null | undefined
): string[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    const names: string[] = [];
    for (const item of list) {
        if ((item.Binding as EntityEntitlementTargetBinding)?.ReadOnly !== true) continue;
        const attr = item.Attribute;
        if (!attr) continue;
        names.push(createEntityRef(String(attr.Id ?? attr.DisplayName ?? '')).displayName);
    }
    return names;
}

/**
 * Get the tier2 attribute and binding for a given attribute name (Id/DisplayName).
 * Returns { attribute, binding } when found; binding is undefined when response only has Attributes (no AttributeBindings).
 */
export function getTier2AttributeBindingForAttribute(
    response: EntityEntitlementForSchemaResponse | null | undefined,
    attributeName: string
): { attribute: EntityEntitlementAttributeItem; binding?: EntityEntitlementSchemaBinding } | null {
    if (!response) return null;
    const want = normalizeAttrNameForMatch(attributeName);
    if (!want) return null;
    const bindings = response.AttributeBindings;
    if (Array.isArray(bindings) && bindings.length > 0) {
        const found = bindings.find((ab) => {
            const attr = ab.Attribute;
            if (!attr) return false;
            const id = normalizeAttrNameForMatch((attr.Id ?? attr.DisplayName ?? '').toString());
            const displayName = normalizeAttrNameForMatch((attr.DisplayName ?? '').toString());
            return id === want || displayName === want;
        });
        if (found?.Attribute) return { attribute: found.Attribute, binding: found.Binding };
        return null;
    }
    const attrs = Array.isArray(response.Attributes) ? response.Attributes : [];
    const found = attrs.find((attr) => {
        const id = normalizeAttrNameForMatch((attr.Id ?? attr.DisplayName ?? '').toString());
        const displayName = normalizeAttrNameForMatch((attr.DisplayName ?? '').toString());
        return id === want || displayName === want;
    });
    return found ? { attribute: found, binding: undefined } : null;
}

/**
 * Format tier2 attribute + optional binding for display in debug tooltips (prettified, sorted by key).
 */
export function formatTier2AttributeForDisplay(
    attribute: EntityEntitlementAttributeItem,
    binding?: EntityEntitlementSchemaBinding
): string {
    const attr = attribute;
    const lines: string[] = [];
    if (attr) {
        lines.push(`Array: ${attr.Array === true}`);
        if (attr.Description != null && String(attr.Description).trim() !== '') lines.push(`Description: ${String(attr.Description).trim().slice(0, 60)}${String(attr.Description).length > 60 ? '…' : ''}`);
        lines.push(`Indexed: ${attr.Indexed === true}`);
        lines.push(`IsReference: ${attr.IsReference === true}`);
        if (Array.isArray(attr.IsReferenceTo) && attr.IsReferenceTo.length > 0) {
            const n = attr.IsReferenceTo.length;
            lines.push(`IsReferenceTo (${n}): ${attr.IsReferenceTo.join(', ')}`);
        }
        lines.push(`Searchable: ${attr.Searchable === true}`);
        if (attr.Type) lines.push(`Type: ${attr.Type}`);
    }
    const b = binding;
    if (b) {
        if (Array.isArray(b.AllowedValues) && b.AllowedValues.length > 0) {
            const vals = b.AllowedValues;
            if (vals.length <= ALLOWED_VALUES_DISPLAY_THRESHOLD) {
                const parts = vals.map((v) => {
                    const s = v !== null && v !== undefined && typeof v === 'object' && ('DisplayName' in v || 'Id' in v)
                        ? String((v as { DisplayName?: string; Id?: string }).DisplayName ?? (v as { DisplayName?: string; Id?: string }).Id ?? JSON.stringify(v))
                        : String(v);
                    return s.length > ALLOWED_VALUE_ITEM_MAX_LENGTH ? `${s.slice(0, ALLOWED_VALUE_ITEM_MAX_LENGTH)}...` : s;
                });
                lines.push(`AllowedValues: ${parts.join(', ')}`);
            } else {
                lines.push(`AllowedValues: ${vals.length} option(s)`);
            }
        }
        if (b.RegEx != null && String(b.RegEx).trim() !== '') {
            const reStr = String(b.RegEx).trim();
            const display = reStr.length > REGEX_DISPLAY_MAX_LENGTH ? `${reStr.slice(0, REGEX_DISPLAY_MAX_LENGTH)}...` : reStr;
            lines.push(`RegEx: ${display}`);
        }
        if (b.Required === true) lines.push('Required: true');
    }
    lines.sort((lineA, lineB) => {
        const keyA = lineA.indexOf(':') >= 0 ? lineA.slice(0, lineA.indexOf(':')).trim() : lineA;
        const keyB = lineB.indexOf(':') >= 0 ? lineB.slice(0, lineB.indexOf(':')).trim() : lineB;
        return keyA.localeCompare(keyB, undefined, { sensitivity: 'base' });
    });
    return lines.join('\n');
}

/**
 * Format a tier3 attribute entitlement item for display in debug tooltips (prettified).
 * Lines are sorted alphabetically by key. Includes ReadOnly (true when ReadValue && !SetValue).
 */
export function formatTargetEntitlementItemForDisplay(item: EntityEntitlementTargetItem): string {
    const attr = item.Attribute;
    const lines: string[] = [];
    lines.push(`AddValue: ${item.AddValue === true}`);
    lines.push(`ReadValue: ${item.ReadValue === true}`);
    const readOnly = item.ReadValue === true && item.SetValue !== true;
    lines.push(`ReadOnly: ${readOnly}`);
    lines.push(`RemoveValue: ${item.RemoveValue === true}`);
    lines.push(`SetValue: ${item.SetValue === true}`);
    if (attr) {
        lines.push(`Array: ${attr.Array === true}`);
        lines.push(`Encrypted: ${attr.Encrypted === true}`);
        lines.push(`HasLengthCount: ${attr.HasLengthCount === true}`);
        lines.push(`Indexed: ${attr.Indexed === true}`);
        if (attr.Length != null) lines.push(`Length: ${attr.Length}`);
        lines.push(`IsReference: ${attr.IsReference === true}`);
        if (Array.isArray(attr.IsReferenceTo) && attr.IsReferenceTo.length > 0) {
            const n = attr.IsReferenceTo.length;
            lines.push(`IsReferenceTo (${n}): ${attr.IsReferenceTo.join(', ')}`);
        }
        lines.push(`Searchable: ${attr.Searchable === true}`);
        if (attr.Type) lines.push(`Type: ${attr.Type}`);
    }
    const b = item.Binding;
    if (b) {
        if (Array.isArray(b.AllowedValues) && b.AllowedValues.length > 0) {
            const vals = b.AllowedValues;
            if (vals.length <= ALLOWED_VALUES_DISPLAY_THRESHOLD) {
                const parts = vals.map((v) => {
                    const s = v !== null && v !== undefined && typeof v === 'object' && ('DisplayName' in v || 'Id' in v)
                        ? String((v as { DisplayName?: string; Id?: string }).DisplayName ?? (v as { DisplayName?: string; Id?: string }).Id ?? JSON.stringify(v))
                        : String(v);
                    return s.length > ALLOWED_VALUE_ITEM_MAX_LENGTH ? `${s.slice(0, ALLOWED_VALUE_ITEM_MAX_LENGTH)}...` : s;
                });
                lines.push(`AllowedValues: ${parts.join(', ')}`);
            } else {
                lines.push(`AllowedValues: ${vals.length} option(s)`);
            }
        }
        if (b.RegEx != null && String(b.RegEx).trim() !== '') {
            const reStr = String(b.RegEx).trim();
            const display = reStr.length > REGEX_DISPLAY_MAX_LENGTH
                ? `${reStr.slice(0, REGEX_DISPLAY_MAX_LENGTH)}...`
                : reStr;
            lines.push(`RegEx: ${display}`);
        }
        if (b.Required === true) lines.push('Required: true');
    }
    lines.sort((a, b) => {
        const keyA = a.indexOf(':') >= 0 ? a.slice(0, a.indexOf(':')).trim() : a;
        const keyB = b.indexOf(':') >= 0 ? b.slice(0, b.indexOf(':')).trim() : b;
        return keyA.localeCompare(keyB, undefined, { sensitivity: 'base' });
    });
    return lines.join('\n');
}

/**
 * Get attribute names to display in read mode: readable and with content (not null/empty).
 * Excludes attributes where ReadValue is true but HasLengthCount is false (null/empty set — no need to show).
 * Returns undefined when no tier3 restriction; otherwise string[] of attribute names to display.
 */
export function getDisplayableAttributeIdsFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] | undefined {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return undefined;
    const names = list
        .filter((e) => e.ReadValue && e.Attribute?.HasLengthCount !== false)
        .map((e) => attributeIdToName(e.Attribute?.Id ?? e.Attribute?.DisplayName ?? ''))
        .filter(Boolean);
    return names;
}

/**
 * Get attribute names that are readable but have HasLengthCount === false (API reports no count/length).
 * If the entity actually has a value for any of these, treat as API inconsistency and show an error.
 */
export function getReadableButNoLengthCountAttributeIds(
    response: EntityEntitlementForTargetResponse | null
): string[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    return list
        .filter((e) => e.ReadValue === true && e.Attribute?.HasLengthCount === false)
        .map((e) => attributeIdToName(e.Attribute?.Id ?? e.Attribute?.DisplayName ?? ''))
        .filter(Boolean);
}

/**
 * From tier2 (per-entity schema), get attribute refs that are non-indexed or have HasLengthCount false.
 * Used when tier3 fails to still show delay-load rows (e.g. Operation) and noLengthCountIds for stub rendering.
 */
export function getNonIndexedOrNoLengthCountAttributeRefsFromTier2(
    response: EntityEntitlementForSchemaResponse | null | undefined
): Array<{ id: string; displayName: string }> {
    const attributes = getTier2AttributesFromResponse(response);
    if (!attributes.length) return [];
    return attributes
        .filter((attr) => {
            const indexed = attr.Indexed;
            const hasLengthCount = (attr as { HasLengthCount?: boolean }).HasLengthCount;
            return indexed === false || hasLengthCount === false;
        })
        .map((attr) => {
            const id = attributeIdToName(attr.Id ?? attr.DisplayName ?? '');
            const displayName = (attr.DisplayName ?? '').toString().trim() || id;
            return { id, displayName };
        })
        .filter((r) => r.id.length > 0);
}

/** Helper: true if item has SetValue (handles API casing SetValue/setValue). */
function tier3ItemSetValue(item: EntityEntitlementTargetItem): boolean {
    return item.SetValue === true || (item as any).setValue === true;
}

/** True if item has ReadValue (handles API casing ReadValue/readValue). */
export function tier3ItemReadValue(item: EntityEntitlementTargetItem): boolean {
    return item.ReadValue === true || (item as any).readValue === true;
}

/** True when tier3 Attribute.Encrypted is true (handles Pascal/camel casing from API). */
export function tier3AttributeEncrypted(attr: EntityEntitlementTargetAttribute | null | undefined): boolean {
    if (!attr) return false;
    return attr.Encrypted === true || (attr as unknown as { encrypted?: boolean }).encrypted === true;
}

/** True when the requestor may call decrypt (mgmt/encdec): tier3 item `Decrypt` / `decrypt` only — not `ApplicableOperations` (that list is not requestor entitlement). */
export function tier3ItemDecryptAllowed(item: EntityEntitlementTargetItem | null | undefined): boolean {
    if (!item) return false;
    return item.Decrypt === true || (item as unknown as { decrypt?: boolean }).decrypt === true;
}

/**
 * Build form-attribute shape from tier3 TargetEntitlements (SetValue only).
 * Used when schema/field options are empty so the upsert dialog can still show fields (e.g. PolicyRule for imperators).
 */
export function buildFormAttributesFromTargetEntitlements(
    response: EntityEntitlementForTargetResponse | null
): FormAttributeOption[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    return list
        .filter((e) => tier3ItemSetValue(e) && e.Attribute)
        .map((e): FormAttributeOption => {
            const attr = e.Attribute!;
            const name = attributeIdToName(attr.Id ?? attr.DisplayName ?? '');
            const type = (attr.Type ?? 'string').toString().toLowerCase();
            const binding = e.Binding;
            return {
                name,
                id: name,
                value: name,
                type,
                label: attr.DisplayName ?? name,
                required: binding?.Required === true,
                multiValued: !!attr.Array,
                isReference: !!attr.IsReference,
                isReferenceTo: Array.isArray(attr.IsReferenceTo) ? attr.IsReferenceTo : undefined,
                computed: false,
                description: attr.Description,
            };
        })
        .filter((a) => a.name.length > 0);
}

/**
 * Build form-attribute shape from tier3 for all readable attributes (ReadValue === true).
 * Use when buildFormAttributesFromTargetEntitlements returns [] (e.g. API uses different casing or only sends ReadValue).
 */
export function buildFormAttributesFromTargetEntitlementsReadable(
    response: EntityEntitlementForTargetResponse | null
): FormAttributeOption[] {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return [];
    return list
        .filter((e) => tier3ItemReadValue(e) && e.Attribute)
        .map((e): FormAttributeOption => {
            const attr = e.Attribute!;
            const name = attributeIdToName(attr.Id ?? attr.DisplayName ?? '');
            const type = (attr.Type ?? 'string').toString().toLowerCase();
            const binding = e.Binding;
            return {
                name,
                id: name,
                value: name,
                type,
                label: attr.DisplayName ?? name,
                required: binding?.Required === true,
                multiValued: !!attr.Array,
                isReference: !!attr.IsReference,
                isReferenceTo: Array.isArray(attr.IsReferenceTo) ? attr.IsReferenceTo : undefined,
                computed: false,
                description: attr.Description,
            };
        })
        .filter((a) => a.name.length > 0);
}

/** Reserved keys we still expose as form attributes (Id, DisplayName, EntityType). */
const ENTITY_KEY_ALIASES: Record<string, string> = { id: 'Id', objectid: 'Id', displayname: 'DisplayName', entitytype: 'EntityType' };

/**
 * Build minimal form attributes from an existing entity's keys (edit mode).
 * Use when we have entity detail (e.g. with reference expansion) and want to avoid fetching tier2 for the attributes list (e.g. RequestApprovalType, PolicyRule, PluginFunction).
 */
export function buildFormAttributesFromEntityKeys(entity: Record<string, unknown>): FormAttributeOption[] {
    if (!entity || typeof entity !== 'object' || Array.isArray(entity)) return [];
    const keys = Object.keys(entity).filter(
        (k) => !k.startsWith('_')
    );
    if (keys.length === 0) return [];
    return keys.map((key): FormAttributeOption => {
        const value = entity[key];
        const name = key;
        const normalized = name.toLowerCase();
        const label = ENTITY_KEY_ALIASES[normalized] ?? name;
        const isRef = value != null && typeof value === 'object' && !Array.isArray(value) && ('Id' in (value as object) || 'id' in (value as object));
        return {
            name,
            id: name,
            value: name,
            label,
            type: isRef ? 'reference' : 'string',
            required: false,
            multiValued: false,
            isReference: isRef,
            computed: false,
        };
    });
}

/**
 * Get attribute names the requestor can set (write) for this target entity.
 */
export function getSettableAttributeIdsFromTargetResponse(
    response: EntityEntitlementForTargetResponse | null
): string[] | undefined {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return undefined;
    const names = list
        .filter((e) => tier3ItemSetValue(e))
        .map((e) => attributeIdToName(e.Attribute?.Id ?? e.Attribute?.DisplayName ?? ''))
        .filter(Boolean);
    return names;
}

/**
 * Whether the requestor has any writable attribute for this target (tier3).
 * True if no tier3 data (no restriction); otherwise true if any attribute has
 * SetValue, AddValue, RemoveValue, or DeleteValue.
 */
export function hasAnyWritableAttribute(
    response: EntityEntitlementForTargetResponse | null
): boolean {
    const list = getTargetEntitlementsList(response);
    if (!list.length) return true;
    return list.some(
        (e) => e.SetValue === true || e.AddValue === true || e.RemoveValue === true || e.DeleteValue === true
    );
}

/**
 * Get the list of attribute names the requestor can read from a per-entity entitlement response.
 * Supports legacy (ReadableAttributeIds), AttributeBindings, and Attributes API shapes.
 * When Entity.IsEntitySet is true (facade, e.g. Person), AttributeBindings are not used.
 * Returns: undefined = no filter (admin or no data), [] = no access, string[] = allowed names.
 */
export function getReadableAttributeIdsFromPerEntityResponse(
    response: EntityEntitlementForSchemaResponse | null
): string[] | undefined {
    if (!response) return undefined;
    if (response.IsAdministrator === true) return undefined;
    if (Array.isArray(response.ReadableAttributeIds)) return response.ReadableAttributeIds;
    // Entity-set facade: no AttributeBindings; use Attributes only
    if (!isEntitySetFacade(response)) {
        const fromBindings = response.AttributeBindings;
        if (Array.isArray(fromBindings) && fromBindings.length > 0) {
            const names = fromBindings.map((ab) => {
                const id = (ab.Attribute?.Id ?? ab.Binding?.Attribute ?? '').toString().trim();
                if (id) return id;
                return (ab.Attribute?.DisplayName ?? '').toString().trim();
            }).filter(Boolean);
            return names;
        }
    }
    const attrs = response.Attributes;
    if (!Array.isArray(attrs)) return undefined;
    const names = attrs.map((a) => {
        const id = (a.Id ?? '').toString().trim();
        if (id) return id;
        return (a.DisplayName ?? '').toString().trim();
    }).filter(Boolean);
    return names;
}

/**
 * Get readable attribute names from per-entity (tier2) response where Attribute.Indexed is true (or omitted).
 * Use for table/list API attribute requests so we do not request large or file-backed non-indexed properties.
 * When Entity.IsEntitySet is true (facade), AttributeBindings are not used.
 * Returns undefined when no filter; otherwise string[] of attribute names to request.
 */
export function getIndexedReadableAttributeIdsFromPerEntityResponse(
    response: EntityEntitlementForSchemaResponse | null
): string[] | undefined {
    if (!response) return undefined;
    if (response.IsAdministrator === true) return undefined;
    if (Array.isArray(response.ReadableAttributeIds)) {
        // Legacy shape has no Indexed per attribute; return as-is (all readable)
        return response.ReadableAttributeIds;
    }
    // Entity-set facade: no AttributeBindings; use Attributes only
    if (!isEntitySetFacade(response)) {
        const fromBindings = response.AttributeBindings;
        if (Array.isArray(fromBindings) && fromBindings.length > 0) {
            const names = fromBindings
                .filter((ab) => ab.Attribute?.Indexed !== false)
                .map((ab) => {
                    const id = (ab.Attribute?.Id ?? ab.Binding?.Attribute ?? '').toString().trim();
                    if (id) return id;
                    return (ab.Attribute?.DisplayName ?? '').toString().trim();
                })
                .filter(Boolean);
            return names;
        }
    }
    const attrs = response.Attributes;
    if (!Array.isArray(attrs)) return undefined;
    const names = attrs
        .filter((a) => (a as EntityEntitlementAttributeItem).Indexed !== false)
        .map((a) => {
            const id = (a.Id ?? '').toString().trim();
            if (id) return id;
            return (a.DisplayName ?? '').toString().trim();
        })
        .filter(Boolean);
    return names;
}

/**
 * Get searchable attribute names from per-entity (tier2) response.
 * - Uses Attribute.Searchable === true from AttributeBindings/Attributes when available.
 * - Falls back to legacy ReadableAttributeIds when searchable metadata is unavailable.
 * Returns undefined when no filter (admin or no data), [] when there are no searchable attributes.
 */
export function getSearchableAttributeIdsFromPerEntityResponse(
    response: EntityEntitlementForSchemaResponse | null
): string[] | undefined {
    if (!response) return undefined;
    if (Array.isArray(response.ReadableAttributeIds)) {
        // Legacy shape has no Searchable per attribute; use readable as best available filter.
        return response.ReadableAttributeIds;
    }

    if (!isEntitySetFacade(response)) {
        const fromBindings = response.AttributeBindings;
        if (Array.isArray(fromBindings) && fromBindings.length > 0) {
            const names = fromBindings
                .filter((ab) => ab.Attribute?.Searchable === true)
                .map((ab) => {
                    const id = (ab.Attribute?.Id ?? ab.Binding?.Attribute ?? '').toString().trim();
                    if (id) return attributeDisplayNameForRequest(id);
                    return attributeDisplayNameForRequest((ab.Attribute?.DisplayName ?? '').toString().trim());
                })
                .filter(Boolean);
            return names;
        }
    }

    const attrs = response.Attributes;
    if (!Array.isArray(attrs)) return undefined;
    const names = attrs
        .filter((a) => (a as EntityEntitlementAttributeItem).Searchable === true)
        .map((a) => {
            const id = (a.Id ?? '').toString().trim();
            if (id) return attributeDisplayNameForRequest(id);
            return attributeDisplayNameForRequest((a.DisplayName ?? '').toString().trim());
        })
        .filter(Boolean);
    return names;
}

/**
 * Get the list of attribute IDs the requestor can read for a given schema entity (for filtering Search In dropdown).
 * Returns undefined when the user is admin or when the API does not provide attribute-level data (show all).
 */
export function getReadableAttributeIdsForEntity(
    response: EntityEntitlementsResponse | null,
    schemaEntityId: string
): string[] | undefined {
    if (!response?.EntityEntitlements?.length || !schemaEntityId?.trim()) return undefined;
    if (response.IsAdministrator) return undefined;
    const id = schemaEntityId.trim();
    const item = response.EntityEntitlements.find(
        (e) => (e.Id || '').toLowerCase() === id.toLowerCase()
    );
    if (!item?.ReadableAttributeIds?.length) return undefined;
    return item.ReadableAttributeIds;
}
